import {CloseOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {CountdownTimer} from '@countdown-timer';
import {BottomSheet, Button, IconButton, OtpInput} from '@ds/components';
import {toast} from '@Toast';
import {useEffect, useRef, useState} from 'react';

type OtpModalProps = {
  title?: string;
  label?: string;
  description?: React.ReactNode;
  submitButtonText?: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (otp: string) => Promise<boolean>;
  sendOtp: () => Promise<void>;
  otpTtl: number;
  setOtpTtl: React.Dispatch<React.SetStateAction<number>>;
};

const OtpModal: React.FC<OtpModalProps> = ({
  title = 'دریافت رمز یکبار مصرف',
  label,
  description,
  submitButtonText = 'تایید و ادامه',
  open,
  onClose,
  onSubmit,
  sendOtp,
  otpTtl,
  setOtpTtl
}) => {
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const hasSentOnOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasSentOnOpenRef.current = false;
      return;
    }

    if (!hasSentOnOpenRef.current && otpTtl === 0) {
      hasSentOnOpenRef.current = true;
      void sendOtp().catch(() => {
        toast('مشکلی پیش آمده. دوباره تلاش کنید!');
      });
    }
  }, [open, otpTtl, sendOtp]);

  useEffect(() => {
    if (!open) {
      setOtp('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      toast('لطفا کد پیامک شده را وارد کنید');
      return;
    }

    setSubmitting(true);

    try {
      const isSuccessful = await onSubmit(otp);
      if (!isSuccessful) {
        setOtp('');
      }
    } catch {
      toast('کد اشتباه است');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      await sendOtp();
    } catch {
      toast('مشکلی پیش آمده. دوباره تلاش کنید!');
    }
  };

  return (
    <BottomSheet
      show={open}
      onClose={onClose}
      header={
        <div className="flex items-center gap-2 px-2 pt-2">
          <IconButton aria-label="بستن" size="md" onClick={onClose}>
            <CloseOutlined aria-hidden />
          </IconButton>
          <h2 className="text-subtitle-md text-neutral-fg">{title}</h2>
        </div>
      }
      footer={
        <div className="border-t border-gray-04 p-2.5">
          <Button fullWidth onClick={handleSubmit} disabled={otp.length !== 6} loading={submitting}>
            {submitButtonText}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {label ? <p className="text-body-sm text-neutral-fg">{label}</p> : null}
        <OtpInput length={6} value={otp} onChange={setOtp} />
        <div className="flex items-center justify-center gap-2.5 text-body-sm text-gray-08">
          {otpTtl > 0 ? (
            <>
              <span>درخواست دوباره رمز تا</span>
              <CountdownTimer initialCounter={otpTtl} setCountDown={setOtpTtl} />
            </>
          ) : (
            <>
              <span>کد را دریافت نکردید؟</span>
              <Button
                className="px-0!"
                onClick={handleResend}
                variant="link"
                color="primary"
                size="sm"
              >
                ارسال دوباره کد
              </Button>
            </>
          )}
        </div>
        {description ? (
          <div className="flex gap-2.5 rounded-xl bg-brand-surface p-4 text-body-sm text-neutral-fg">
            <InfoCircleOutlined aria-hidden className="mt-1 shrink-0 text-body-lg" />
            <div>{description}</div>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
};

export default OtpModal;
